import type { HTMLAttributes, JSX } from "react";
import { createElement, useEffect, useRef } from "react";

import { type ITelemetryCustom, sendTelemetryEvent } from "@/lib/telemetry";

interface ITelemetryBoundary extends HTMLAttributes<HTMLElement> {
    telemetryCustom?: ITelemetryCustom;
    as?: keyof JSX.IntrinsicElements;
}

let sharedObserver: IntersectionObserver | null = null;
const observerCallbacks = new WeakMap<Element, () => void>();

function getSharedObserver(): IntersectionObserver {
    if (!sharedObserver) {
        sharedObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const callback = observerCallbacks.get(entry.target);
                        if (callback) {
                            callback();
                            sharedObserver?.unobserve(entry.target);
                            observerCallbacks.delete(entry.target);
                        }
                    }
                });
            },
            {
                threshold: 0.15,
                rootMargin: "20px 0px 20px 0px",
            },
        );
    }
    return sharedObserver;
}

export function TelemetryBoundary({ telemetryCustom, as = "div", children, ...props }: ITelemetryBoundary) {
    const ref = useRef<Element | null>(null);
    const hasFiredRef = useRef(false);

    useEffect(() => {
        const element = ref.current;
        if (!element || hasFiredRef.current) return;

        const observer = getSharedObserver();

        const callback = () => {
            if (!hasFiredRef.current && telemetryCustom) {
                hasFiredRef.current = true;
                sendTelemetryEvent(telemetryCustom);
            }
        };

        observerCallbacks.set(element, callback);
        observer.observe(element);

        return () => {
            if (element) {
                observer.unobserve(element);
                observerCallbacks.delete(element);
            }
        };
    }, [telemetryCustom]);

    return createElement(as, { ...props, ref }, children);
}
